import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  ArrowPathIcon,
  ArrowDownTrayIcon,
  CloudArrowUpIcon,
  CommandLineIcon,
  CpuChipIcon,
  HeartIcon,
  ClockIcon,
  GlobeAltIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import adminApi, {
  GtsSyncStatus,
  HealthSummary,
  LogTail,
  ScheduledJob,
  SystemInfo,
} from '../../api/admin';
import { auth } from '../../api/axios';
import { useLanguage } from '../../contexts/LanguageContext';

type Tab = 'logs' | 'system' | 'health' | 'gts' | 'jobs';

const LOG_LEVELS = ['ERROR', 'WARN', 'INFO', 'DEBUG'];

const Ops: React.FC = () => {
  const { t } = useLanguage();
  const ops = t.admin?.ops;
  const [tab, setTab] = useState<Tab>('logs');

  const tabs: { key: Tab; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
    { key: 'logs', label: ops?.tabs?.logs ?? 'Console', icon: CommandLineIcon },
    { key: 'system', label: ops?.tabs?.system ?? 'Système', icon: CpuChipIcon },
    { key: 'health', label: ops?.tabs?.health ?? 'Santé', icon: HeartIcon },
    { key: 'gts', label: ops?.tabs?.gts ?? 'Green Target', icon: GlobeAltIcon },
    { key: 'jobs', label: ops?.tabs?.jobs ?? 'Tâches', icon: ClockIcon },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">{ops?.title ?? 'Ops'}</h1>
        <p className="text-sm text-slate-500">
          {ops?.subtitle ?? 'Logs serveur, état du système et tâches planifiées.'}
        </p>
      </div>

      <div className="flex gap-1 border-b border-slate-200">
        {tabs.map((tb) => (
          <button
            key={tb.key}
            onClick={() => setTab(tb.key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === tb.key
                ? 'border-indigo-600 text-indigo-700'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            <tb.icon className="h-4 w-4" />
            {tb.label}
          </button>
        ))}
      </div>

      {tab === 'logs' && <LogsPanel />}
      {tab === 'system' && <SystemPanel />}
      {tab === 'health' && <HealthPanel />}
      {tab === 'gts' && <GtsPanel />}
      {tab === 'jobs' && <JobsPanel />}
    </div>
  );
};

const LogsPanel: React.FC = () => {
  const { t } = useLanguage();
  const l = t.admin?.ops?.logs;
  const [data, setData] = useState<LogTail | null>(null);
  const [level, setLevel] = useState('');
  const [search, setSearch] = useState('');
  const [limit, setLimit] = useState(200);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const load = useCallback(() => {
    setLoading(true);
    adminApi
      .getLogs({ limit, level: level || undefined, q: search || undefined })
      .then(setData)
      .catch((e) => toast.error(e?.response?.data?.message || 'Erreur'))
      .finally(() => setLoading(false));
  }, [limit, level, search]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
  }, [autoRefresh, load]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [data]);

  const download = () => {
    const token = auth.getAccessToken();
    fetch(adminApi.logsDownloadUrl(), {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => {
        if (!r.ok) throw new Error('download failed');
        return r.blob();
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'tunifact.log';
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error('Erreur'));
  };

  const lineColor = (line: string) => {
    if (/\bERROR\b/.test(line)) return 'text-red-400';
    if (/\bWARN\b/.test(line)) return 'text-amber-300';
    if (/\bDEBUG\b/.test(line)) return 'text-slate-500';
    return 'text-slate-300';
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={level}
          onChange={(e) => setLevel(e.target.value)}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
        >
          <option value="">{l?.allLevels ?? 'Tous les niveaux'}</option>
          {LOG_LEVELS.map((lv) => (
            <option key={lv} value={lv}>
              {lv}
            </option>
          ))}
        </select>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={l?.search ?? 'Rechercher…'}
          className="flex-1 min-w-[12rem] rounded-lg border border-slate-200 px-3 py-2 text-sm"
        />
        <select
          value={limit}
          onChange={(e) => setLimit(Number(e.target.value))}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
        >
          {[100, 200, 500, 1000, 2000].map((n) => (
            <option key={n} value={n}>
              {n} {l?.lines ?? 'lignes'}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
          />
          {l?.autoRefresh ?? 'Auto'}
        </label>
        <button
          onClick={load}
          className="flex items-center gap-1.5 rounded-lg bg-slate-100 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200"
        >
          <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          {l?.refresh ?? 'Rafraîchir'}
        </button>
        <button
          onClick={download}
          className="flex items-center gap-1.5 rounded-lg bg-slate-100 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200"
        >
          <ArrowDownTrayIcon className="h-4 w-4" />
          {l?.download ?? 'Télécharger'}
        </button>
      </div>

      {data && !data.available && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
          {l?.unavailable ?? 'Fichier de log introuvable.'} <span className="font-mono">{data.file}</span>
        </div>
      )}

      <div
        ref={scrollRef}
        className="bg-slate-900 rounded-xl p-4 h-[60vh] overflow-auto font-mono text-xs leading-relaxed"
      >
        {data && data.lines.length === 0 && (
          <div className="text-slate-500">{l?.empty ?? 'Aucune ligne de log.'}</div>
        )}
        {data?.lines.map((line, i) => (
          <div key={i} className={`whitespace-pre-wrap break-all ${lineColor(line)}`}>
            {line}
          </div>
        ))}
      </div>
      {data && (
        <div className="text-xs text-slate-400">
          {data.returnedLines} {l?.returned ?? 'lignes'}
        </div>
      )}
    </div>
  );
};

const SystemPanel: React.FC = () => {
  const { t } = useLanguage();
  const s = t.admin?.ops?.system;
  const [info, setInfo] = useState<SystemInfo | null>(null);

  useEffect(() => {
    adminApi
      .getSystemInfo()
      .then(setInfo)
      .catch((e) => toast.error(e?.response?.data?.message || 'Erreur'));
  }, []);

  if (!info) return <div className="text-slate-500">{t.common.loading}</div>;

  const fmtBytes = (b: number) => `${(b / 1024 / 1024).toFixed(0)} MB`;
  const fmtUptime = (ms: number) => {
    const sec = Math.floor(ms / 1000);
    const d = Math.floor(sec / 86400);
    const h = Math.floor((sec % 86400) / 3600);
    const m = Math.floor((sec % 3600) / 60);
    return `${d > 0 ? `${d}j ` : ''}${h}h ${m}m`;
  };

  const rows: [string, React.ReactNode][] = [
    [s?.appName ?? 'Application', info.appName],
    [s?.profiles ?? 'Profils', info.activeProfiles.join(', ') || 'default'],
    [s?.uptime ?? 'Uptime', fmtUptime(info.uptimeMillis)],
    [s?.startedAt ?? 'Démarré le', new Date(info.startedAt).toLocaleString()],
    [s?.java ?? 'Java', info.javaVersion],
    [s?.os ?? 'OS', info.osName],
    [s?.processors ?? 'Processeurs', String(info.availableProcessors)],
    [s?.threads ?? 'Threads', String(info.threadCount)],
    [
      s?.heap ?? 'Heap',
      `${fmtBytes(info.heapUsedBytes)} / ${info.heapMaxBytes > 0 ? fmtBytes(info.heapMaxBytes) : '∞'}`,
    ],
  ];

  return (
    <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
      {rows.map(([label, value]) => (
        <div key={label} className="flex items-center justify-between px-5 py-3 text-sm">
          <span className="text-slate-500">{label}</span>
          <span className="font-medium text-slate-900 font-mono">{value}</span>
        </div>
      ))}
    </div>
  );
};

const HealthPanel: React.FC = () => {
  const { t } = useLanguage();
  const h = t.admin?.ops?.health;
  const [health, setHealth] = useState<HealthSummary | null>(null);

  useEffect(() => {
    adminApi
      .getHealth()
      .then(setHealth)
      .catch((e) => toast.error(e?.response?.data?.message || 'Erreur'));
  }, []);

  if (!health) return <div className="text-slate-500">{t.common.loading}</div>;

  const badge = (status: string) =>
    status === 'UP'
      ? 'bg-green-100 text-green-800'
      : status === 'DOWN'
      ? 'bg-red-100 text-red-800'
      : 'bg-slate-100 text-slate-700';

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-slate-200 px-5 py-4 flex items-center justify-between">
        <span className="text-sm font-medium text-slate-600">{h?.status ?? 'État global'}</span>
        <span className={`text-sm font-semibold rounded-full px-3 py-1 ${badge(health.status)}`}>
          {health.status}
        </span>
      </div>

      {Object.keys(health.components).length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
          <div className="px-5 py-3 text-xs font-semibold uppercase text-slate-400">
            {h?.components ?? 'Composants'}
          </div>
          {Object.entries(health.components).map(([name, status]) => (
            <div key={name} className="flex items-center justify-between px-5 py-3 text-sm">
              <span className="text-slate-700 font-mono">{name}</span>
              <span className={`text-xs font-semibold rounded-full px-2.5 py-0.5 ${badge(status)}`}>
                {status}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const GtsPanel: React.FC = () => {
  const { t } = useLanguage();
  const g = t.admin?.ops?.gts;
  const [status, setStatus] = useState<GtsSyncStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    adminApi
      .getGtsStatus()
      .then(setStatus)
      .catch((e) => toast.error(e?.response?.data?.message || 'Erreur'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const runSync = useCallback(() => {
    setSyncing(true);
    adminApi
      .triggerGtsSync()
      .then((res) => {
        setStatus(res.status);
        toast.success(
          `${g?.syncDone ?? 'Synchronisation effectuée'} — ${res.backfilled} ${
            g?.backfilledLabel ?? 'ajoutées'
          }, ${res.publishedNow} ${g?.publishedNowLabel ?? 'publiées'}`
        );
      })
      .catch((e) => toast.error(e?.response?.data?.message || 'Erreur'))
      .finally(() => setSyncing(false));
  }, [g]);

  if (!status) return <div className="text-slate-500">{t.common.loading}</div>;

  const Bool: React.FC<{ ok: boolean; okLabel?: string; koLabel?: string }> = ({
    ok,
    okLabel,
    koLabel,
  }) => (
    <span
      className={`inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2.5 py-0.5 ${
        ok ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
      }`}
    >
      {ok ? <CheckCircleIcon className="h-3.5 w-3.5" /> : <XCircleIcon className="h-3.5 w-3.5" />}
      {ok ? okLabel ?? g?.yes ?? 'Oui' : koLabel ?? g?.no ?? 'Non'}
    </span>
  );

  const fmtDate = (d: string | null) =>
    d ? new Date(d).toLocaleString() : g?.never ?? 'Jamais';

  // Overall verdict: configured AND reachable AND no stuck/failed events.
  const healthy = status.configured && status.reachable && status.failed === 0;

  return (
    <div className="space-y-4">
      {/* Verdict banner */}
      <div
        className={`rounded-xl border px-5 py-4 flex items-center gap-3 ${
          healthy
            ? 'bg-green-50 border-green-200'
            : status.reachable && status.configured
            ? 'bg-amber-50 border-amber-200'
            : 'bg-red-50 border-red-200'
        }`}
      >
        {healthy ? (
          <CheckCircleIcon className="h-6 w-6 text-green-600 flex-shrink-0" />
        ) : (
          <XCircleIcon className="h-6 w-6 text-amber-600 flex-shrink-0" />
        )}
        <div>
          <div className="font-semibold text-slate-900">
            {healthy
              ? g?.verdictOk ?? 'Synchronisation opérationnelle avec Green Target'
              : status.configured
              ? g?.verdictWarn ?? 'Configuré, mais attention requise'
              : g?.verdictKo ?? 'Non configuré ou injoignable'}
          </div>
          <div className="text-sm text-slate-500">
            {status.gtsBaseUrl}
            {status.latencyMs != null && ` · ${status.latencyMs} ms`}
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={runSync}
            disabled={syncing || !status.bridgeEnabled}
            title={!status.bridgeEnabled ? g?.syncDisabledHint ?? 'Pont REST désactivé' : undefined}
            className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <CloudArrowUpIcon className={`h-4 w-4 ${syncing ? 'animate-pulse' : ''}`} />
            {syncing ? g?.syncing ?? 'Synchronisation…' : g?.sync ?? 'Synchroniser'}
          </button>
          <button
            onClick={load}
            className="flex items-center gap-1.5 rounded-lg bg-white/70 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-white"
          >
            <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            {g?.refresh ?? 'Rafraîchir'}
          </button>
        </div>
      </div>

      {/* Configuration */}
      <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
        <div className="px-5 py-3 text-xs font-semibold uppercase text-slate-400">
          {g?.configTitle ?? 'Configuration'}
        </div>
        <div className="flex items-center justify-between px-5 py-3 text-sm">
          <span className="text-slate-600">{g?.bridgeEnabled ?? 'Pont REST activé'}</span>
          <Bool ok={status.bridgeEnabled} />
        </div>
        <div className="flex items-center justify-between px-5 py-3 text-sm">
          <span className="text-slate-600">{g?.ssoConfigured ?? 'Secret SSO partagé configuré'}</span>
          <Bool ok={status.ssoConfigured} />
        </div>
        <div className="flex items-center justify-between px-5 py-3 text-sm">
          <span className="text-slate-600">{g?.kafkaEnabled ?? 'Transport Kafka'}</span>
          <Bool ok={status.kafkaEnabled} okLabel={g?.active ?? 'Actif'} koLabel={g?.off ?? 'Désactivé'} />
        </div>
        <div className="flex items-center justify-between gap-4 px-5 py-3 text-sm">
          <span className="text-slate-600 flex-shrink-0">{g?.gtsBaseUrl ?? 'URL API GTS'}</span>
          <span className="font-mono text-slate-900 text-right break-all">{status.gtsBaseUrl || '—'}</span>
        </div>
        <div className="flex items-center justify-between gap-4 px-5 py-3 text-sm">
          <span className="text-slate-600 flex-shrink-0">{g?.gtsSsoBaseUrl ?? 'URL SSO GTS'}</span>
          <span className="font-mono text-slate-900 text-right break-all">{status.gtsSsoBaseUrl || '—'}</span>
        </div>
      </div>

      {/* Connectivity */}
      <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
        <div className="px-5 py-3 text-xs font-semibold uppercase text-slate-400">
          {g?.connTitle ?? 'Connectivité'}
        </div>
        <div className="flex items-center justify-between px-5 py-3 text-sm">
          <span className="text-slate-600">{g?.reachable ?? 'GTS joignable'}</span>
          <Bool ok={status.reachable} />
        </div>
        {status.latencyMs != null && (
          <div className="flex items-center justify-between px-5 py-3 text-sm">
            <span className="text-slate-600">{g?.latency ?? 'Latence'}</span>
            <span className="font-mono text-slate-900">{status.latencyMs} ms</span>
          </div>
        )}
        {status.httpStatus != null && (
          <div className="flex items-center justify-between px-5 py-3 text-sm">
            <span className="text-slate-600">{g?.httpStatus ?? 'Statut HTTP'}</span>
            <span className="font-mono text-slate-900">{status.httpStatus}</span>
          </div>
        )}
        {status.connectivityError && (
          <div className="px-5 py-3 text-sm text-red-700 bg-red-50 font-mono break-all">
            {status.connectivityError}
          </div>
        )}
      </div>

      {/* Sync / outbox */}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="px-5 py-3 text-xs font-semibold uppercase text-slate-400">
          {g?.syncTitle ?? 'File de synchronisation (outbox)'}
        </div>
        <div className="grid grid-cols-3 divide-x divide-slate-100 border-t border-slate-100">
          <div className="px-5 py-4 text-center">
            <div className="text-2xl font-bold text-amber-600">{status.pending}</div>
            <div className="text-xs text-slate-500 mt-1">{g?.pending ?? 'En attente'}</div>
          </div>
          <div className="px-5 py-4 text-center">
            <div className="text-2xl font-bold text-green-600">{status.published}</div>
            <div className="text-xs text-slate-500 mt-1">{g?.published ?? 'Publiés'}</div>
          </div>
          <div className="px-5 py-4 text-center">
            <div className={`text-2xl font-bold ${status.failed > 0 ? 'text-red-600' : 'text-slate-400'}`}>
              {status.failed}
            </div>
            <div className="text-xs text-slate-500 mt-1">{g?.failed ?? 'Échecs'}</div>
          </div>
        </div>
        <div className="border-t border-slate-100 divide-y divide-slate-100">
          <div className="flex items-center justify-between px-5 py-3 text-sm">
            <span className="text-slate-600">{g?.lastPublished ?? 'Dernière synchro réussie'}</span>
            <span className="text-slate-900">{fmtDate(status.lastPublishedAt)}</span>
          </div>
          <div className="flex items-center justify-between px-5 py-3 text-sm">
            <span className="text-slate-600">{g?.oldestPending ?? 'Plus ancien événement en attente'}</span>
            <span className="text-slate-900">{fmtDate(status.oldestPendingAt)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const JobsPanel: React.FC = () => {
  const { t } = useLanguage();
  const j = t.admin?.ops?.jobs;
  const [jobs, setJobs] = useState<ScheduledJob[]>([]);
  const [running, setRunning] = useState<string | null>(null);

  const load = useCallback(() => {
    adminApi
      .listJobs()
      .then(setJobs)
      .catch((e) => toast.error(e?.response?.data?.message || 'Erreur'));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const run = (job: ScheduledJob) => {
    if (!window.confirm(j?.confirm ?? 'Exécuter cette tâche maintenant ?')) return;
    setRunning(job.id);
    adminApi
      .runJob(job.id)
      .then(() => {
        toast.success(`${j?.triggered ?? 'Tâche lancée'} : ${job.name}`);
        load();
      })
      .catch((e) => toast.error(e?.response?.data?.message || 'Erreur'))
      .finally(() => setRunning(null));
  };

  return (
    <div className="space-y-3">
      {jobs.map((job) => (
        <div
          key={job.id}
          className="bg-white rounded-xl border border-slate-200 p-5 flex items-start justify-between gap-4"
        >
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-slate-900">{job.name}</h3>
              <span
                className={`text-xs font-semibold rounded-full px-2 py-0.5 ${
                  job.enabled ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-500'
                }`}
              >
                {job.enabled ? j?.enabled ?? 'Activée' : j?.disabled ?? 'Désactivée'}
              </span>
            </div>
            <p className="text-sm text-slate-500 mt-1">{job.description}</p>
            <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-400">
              <span>
                {j?.schedule ?? 'Planification'} :{' '}
                <span className="font-mono text-slate-600">{job.schedule}</span>
              </span>
              {job.nextRun && (
                <span>
                  {j?.nextRun ?? 'Prochaine'} :{' '}
                  <span className="text-slate-600">{new Date(job.nextRun).toLocaleString()}</span>
                </span>
              )}
            </div>
          </div>
          {job.triggerable && (
            <button
              onClick={() => run(job)}
              disabled={running === job.id}
              className="flex-shrink-0 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {running === job.id ? j?.running ?? 'En cours…' : j?.run ?? 'Exécuter'}
            </button>
          )}
        </div>
      ))}
    </div>
  );
};

export default Ops;
